import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { Menu, Phone, X, ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import logo from "@/assets/amigo-logo.png";
import {
  ADDRESS_ONE_LINE,
  BOOKING_URL,
  BUSINESS,
  MAPS_URL,
  NAV_LINKS,
  routeTo,
  TOUR_360_URL,
} from "@/lib/site";

/** Accessible mobile menu drawer. */
export function MobileNavigation() {
  const [open, setOpen] = useState(false);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          type="button"
          aria-label="Open menu"
          className="inline-flex size-11 items-center justify-center rounded-sm border border-forest/25 text-forest-deep transition-colors hover:bg-secondary lg:hidden"
        >
          <Menu className="size-5" aria-hidden="true" />
        </button>
      </SheetTrigger>

      <SheetContent
        side="right"
        className="w-[88vw] max-w-sm border-l-0 bg-cream p-0 [&>button]:hidden"
      >
        <SheetTitle className="sr-only">Site menu</SheetTitle>

        <div className="flex items-center justify-between border-b border-sand px-5 py-4">
          <img
            src={logo}
            alt="Amigo Inn & RV Park"
            width={150}
            height={50}
            className="h-9 w-auto"
          />
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label="Close menu"
            className="inline-flex size-11 items-center justify-center rounded-sm text-forest-deep transition-colors hover:bg-secondary"
          >
            <X className="size-5" aria-hidden="true" />
          </button>
        </div>

        <nav aria-label="Mobile" className="px-5 py-4">
          <ul className="divide-y divide-sand/70">
            {NAV_LINKS.map((item) => (
              <li key={item.href}>
                <Link
                  to={routeTo(item.href)}
                  onClick={() => setOpen(false)}
                  activeProps={{ className: "text-forest" }}
                  className="flex min-h-[52px] items-center font-display text-xl text-forest-deep"
                >
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <div className="space-y-3 px-5 pb-6">
          <Button asChild variant="book" size="cta" className="w-full">
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
              Book Your Stay
            </a>
          </Button>
          <Button asChild variant="outlineForest" size="cta" className="w-full">
            <a href={BUSINESS.phoneHref}>
              <Phone aria-hidden="true" /> Call {BUSINESS.phone}
            </a>
          </Button>
          {/* TODO: verify 360° tour URL before production deployment */}
          <a
            href={TOUR_360_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="link-underline text-sm font-semibold text-forest"
          >
            Take the 360° Tour <ArrowUpRight className="size-4" aria-hidden="true" />
          </a>
        </div>

        <div className="mt-auto border-t border-sand px-5 py-5 text-sm text-muted-foreground">
          <a href={MAPS_URL} target="_blank" rel="noopener noreferrer" className="link-underline">
            {ADDRESS_ONE_LINE}
          </a>
          <p className="mt-2">Office hours {BUSINESS.officeHours}</p>
        </div>
      </SheetContent>
    </Sheet>
  );
}
